/**
 * LD Horizontal Scrollbar - Custom scrollbar for horizontal scrolling containers
 * =================================================
 * 
 * This module provides a custom scrollbar indicator for horizontal scrolling
 * containers, commonly used in mobile image galleries and carousels.
 */

class HorizontalScrollbar {
  constructor(container, options = {}) {
    this.container = container;
    this.options = {
      barWidth: options.barWidth || 60,
      barHeight: options.barHeight || 1,
      barColor: options.barColor || 'var(--ld-black-color)',
      barPosition: options.barPosition || 'bottom',
      transitionDuration: options.transitionDuration || '0.3s',
      ...options
    };
    
    this.isInitialized = false;
    this.scrollbarElement = null;
    this.resizeObserver = null;
    
    this.init();
  }
  
  init() {
    if (this.isInitialized || !this.container) return;
    
    this.createScrollbar();
    this.bindEvents();
    this.updateScrollbar();
    this.isInitialized = true;
  }
  
  createScrollbar() {
    // Create scrollbar element
    this.scrollbarElement = document.createElement('div');
    this.scrollbarElement.className = 'ld-horizontal-scrollbar';
    
    // Calculate container position for fixed positioning
    const containerRect = this.container.getBoundingClientRect();
    const containerBottom = containerRect.bottom;
    
    // Apply styles - fixed positioning but aligned to container bottom
    Object.assign(this.scrollbarElement.style, {
      content: '""',
      position: 'fixed',
      bottom: `${window.innerHeight - containerBottom}px`, // Align to container bottom
      left: `${containerRect.left}px`, // Align to container left
      width: `${this.options.barWidth}px`,
      height: `${this.options.barHeight}px`,
      background: this.options.barColor,
      transition: `transform ${this.options.transitionDuration} ease`,
      transform: 'translateX(0px)',
      pointerEvents: 'none',
      zIndex: '10'
    });
    
    // Add to body instead of container
    document.body.appendChild(this.scrollbarElement);
  }
  
  bindEvents() {
    // Scroll event with throttling
    let ticking = false;
    const updateScrollbar = () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          this.updateScrollbar();
          ticking = false;
        });
        ticking = true;
      }
    };
    
    this.container.addEventListener('scroll', updateScrollbar, { passive: true });
    
    // Resize observer for dynamic content changes
    this.resizeObserver = new ResizeObserver(() => {
      this.updateScrollbar();
    });
    this.resizeObserver.observe(this.container);
    
    // Window resize and scroll to update scrollbar position
    const updateScrollbarPosition = () => {
      this.updateScrollbarPosition();
      this.updateScrollbar();
    };
    
    window.addEventListener('resize', updateScrollbarPosition, { passive: true });
    window.addEventListener('scroll', updateScrollbarPosition, { passive: true });
  }
  
  updateScrollbar() {
    if (!this.scrollbarElement) return;
    
    const scrollLeft = this.container.scrollLeft;
    const scrollWidth = this.container.scrollWidth;
    const clientWidth = this.container.clientWidth;
    const maxScroll = scrollWidth - clientWidth;
    
    if (maxScroll <= 0) {
      this.scrollbarElement.style.transform = 'translateX(0px)';
      return;
    }
    
    // Calculate scrollbar position within viewport width
    const scrollPercentage = scrollLeft / maxScroll;
    const viewportWidth = window.innerWidth;
    const scrollbarTrackWidth = viewportWidth - 40 - this.options.barWidth; // 40px = left + right margins
    const scrollbarPosition = scrollPercentage * scrollbarTrackWidth;
    
    // Update scrollbar position
    this.scrollbarElement.style.transform = `translateX(${scrollbarPosition}px)`;
  }
  
  updateScrollbarPosition() {
    if (!this.scrollbarElement) return;
    
    const containerRect = this.container.getBoundingClientRect();
    const containerBottom = containerRect.bottom;
    
    // Update scrollbar position to stay aligned with container
    this.scrollbarElement.style.bottom = `${window.innerHeight - containerBottom}px`;
    this.scrollbarElement.style.left = `${containerRect.left}px`;
  }
  
  destroy() {
    if (this.scrollbarElement) {
      this.scrollbarElement.remove();
      this.scrollbarElement = null;
    }
    
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
      this.resizeObserver = null;
    }
    
    this.isInitialized = false;
  }
}

// Auto-initialize for mobile devices
function initHorizontalScrollbars() {
  // Only initialize on mobile
  if (window.innerWidth > 959) return;
  
  const imageContainers = document.querySelectorAll('.ld-product-page__images');
  
  imageContainers.forEach(container => {
    new HorizontalScrollbar(container, {
      barWidth: 60,
      barHeight: 1,
      barColor: 'var(--ld-black-color)',
      barPosition: 'bottom'
    });
  });
}

// Initialize when DOM is ready
document.addEventListener('DOMContentLoaded', initHorizontalScrollbars);

// Export for manual initialization
window.HorizontalScrollbar = HorizontalScrollbar;
window.initHorizontalScrollbars = initHorizontalScrollbars;
